import { useEffect, useState } from 'react';

import {
  getPublicEndpoints,
  getPublicIncidents,
  getPublicMaintenance,
  getPublicEndpointById,
  getAllEndpointsHistory,
} from '../../../shared/api/publicApi';
import { getSettings } from '../../../shared/api/authApi';
import { parseServerTime } from '../../../shared/utils/format';

const checkedAtMs = (value) => parseServerTime(value)?.getTime() || 0;

// A poll already in flight when a socket result arrived carries the older
// check; keep whichever latest result is newer.
function mergeLatest(prev, fetched) {
  const prevById = new Map(prev.map((ep) => [ep.id, ep]));
  return fetched.map((ep) => {
    const current = prevById.get(ep.id)?.latest;
    if (current && checkedAtMs(current.checked_at) > checkedAtMs(ep.latest?.checked_at)) {
      return { ...ep, latest: current };
    }
    return ep;
  });
}

export default function useStatusData(socket, selectedEndpoint) {
  const [endpoints, setEndpoints] = useState([]);
  const [categories, setCategories] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [endpointDetails, setEndpointDetails] = useState(null);
  const [typeStats, setTypeStats] = useState(null);
  const [pingStats, setPingStats] = useState(null);
  const [siteTitle, setSiteTitle] = useState('Status Page');
  const [branding, setBranding] = useState({ logoUrl: '', tagline: '' });
  const [endpointHistory, setEndpointHistory] = useState({}); // { [id]: [{date, uptime, checks}] }

  useEffect(() => {
    async function fetchData() {
      try {
        setError(null);

        const [endpointsRes, incidentsRes, maintenanceRes, settingsRes, historyRes] = await Promise.all([
          getPublicEndpoints(),
          getPublicIncidents(),
          getPublicMaintenance(),
          getSettings().catch(() => ({ data: { title: 'Status Page' } })),
          getAllEndpointsHistory(90).catch(() => ({ data: { data: {} } })),
        ]);

        if (Array.isArray(endpointsRes.data)) {
          setEndpoints((prev) => mergeLatest(prev, endpointsRes.data));
          setCategories([]);
        } else {
          setEndpoints((prev) => mergeLatest(prev, endpointsRes.data.endpoints || []));
          setCategories(endpointsRes.data.categories || []);
        }

        setIncidents(Array.isArray(incidentsRes.data) ? incidentsRes.data : []);
        setMaintenance(Array.isArray(maintenanceRes.data) ? maintenanceRes.data : []);
        setEndpointHistory(historyRes.data?.data || {});

        if (settingsRes.data?.title) {
          setSiteTitle(settingsRes.data.title);
        }
        setBranding({
          logoUrl: settingsRes.data?.logoUrl || '',
          tagline: settingsRes.data?.tagline || '',
        });
      } catch (err) {
        setError('Failed to load status page');
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();

    if (socket) {
      socket.on('checkResult', (data) => {
        setEndpoints((prev) =>
          prev.map((ep) =>
            ep.id === data.endpoint_id
              ? {
                  ...ep,
                  latest: {
                    status: data.status,
                    response_time: data.responseTime,
                    checked_at: data.checked_at,
                  },
                }
              : ep
          )
        );
      });

      socket.on('incidentCreated', (incident) => {
        setIncidents((prev) => {
          if (prev.find((i) => i.id === incident.id)) return prev;
          return [incident, ...prev];
        });
      });

      socket.on('incidentUpdated', (incident) => {
        setIncidents((prev) => prev.map((i) => (i.id === incident.id ? { ...i, ...incident } : i)));
      });

      socket.on('incidentResolved', (incident) => {
        setIncidents((prev) => prev.map((i) => (i.id === incident.id ? { ...i, ...incident } : i)));
      });

      socket.on('usageUpdated', ({ endpoint_id: endpointId, usage }) => {
        setEndpoints((prev) => prev.map((ep) => (ep.id === endpointId ? { ...ep, usage } : ep)));
        setEndpointDetails((prev) => (prev && prev.id === endpointId ? { ...prev, usage } : prev));
      });
    }

    const interval = setInterval(fetchData, 30000);

    return () => {
      clearInterval(interval);
      if (socket) {
        socket.off('checkResult');
        socket.off('incidentCreated');
        socket.off('incidentUpdated');
        socket.off('incidentResolved');
        socket.off('usageUpdated');
      }
    };
  }, [socket]);

  useEffect(() => {
    if (!selectedEndpoint) {
      setEndpointDetails(null);
      setTypeStats(null);
      setPingStats(null);
      return;
    }

    async function fetchEndpointDetails() {
      try {
        const response = await getPublicEndpointById(selectedEndpoint);
        const endpointData = response.data.endpoint || response.data;
        const results = response.data.results || endpointData.results || [];
        setEndpointDetails({ ...endpointData, results });
        setTypeStats(response.data.typeStats || null);
        setPingStats(response.data.pingStats || null);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load endpoint details:', err);
      }
    }

    fetchEndpointDetails();
  }, [selectedEndpoint]);

  return {
    endpoints,
    categories,
    incidents,
    maintenance,
    loading,
    error,
    endpointDetails,
    typeStats,
    pingStats,
    siteTitle,
    branding,
    endpointHistory,
  };
}
